import { Injectable } from '@angular/core';
import { digger } from '@cbre/digger';
import { DataObject } from '@shared/models/data-object.model';
import { EntityType } from '@shared/models/entity-enum';
import {
  EntityActivityLog,
  EntityRecordActivity,
  EntityRecordActivityLog,
  EntityRecordReqFields,
} from '@shared/models/entity-records/entities-update.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { makeLeaseSpaceTemplatedRequest, makeTemplatedRequest } from '@shared/utils/helpers/edp-request-builder';
import { simpleValueValidation } from '@shared/utils/helpers/records.helpers';
import deepmerge from 'deepmerge';
import { concat, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap, toArray } from 'rxjs/operators';
import { SharedService } from './shared.service';
import { deepCloneObject, deepMerge, RawColumn } from '@miq/catalog';
import { SubTabsFilterOptions } from '@records/records-search/records-details/shared/common.model';
import { Store } from '@ngxs/store';

const DIRECT_SPACE_PAYLOAD_MAX_RECORDS = 5;

type ColumnFieldAndParent = Pick<RawColumn, 'field' | 'parent'>;

export interface ActivityLogSummary {
  id: number;
  method: string;
  fieldName: string;
}

export interface BulkEditSaveResult {
  direct?: boolean;
  hasErrors: boolean;
  failedFieldsByRecordId?: Record<string, string[]>;
}

@Injectable()
export class EdpRecordsUpdateService {
  public entityActivityLog: EntityActivityLog = {};
  public digestedRequests = { h: [] };
  private isTrustedUser = false;

  /**
   * Concat edp requests and map failure responses into common object.
   *
   * @param requests EDP requests.
   */
  public static concatEDPRequestsAndMapFailed(requests: Array<Observable<any>>) {
    return concat(...requests).pipe(
      toArray(),
      map(responseArray => responseArray.filter(res => simpleValueValidation(res['failed'])).map(res => res['failed']))
    );
  }

  constructor(
    public httpService: SharedService,
    public userInfoResolver: UserInfoResolver,
    public store: Store
  ) {
    this.userInfoResolver.hasEDPWriteAccess().then(hasEdpWriteAccess => (this.isTrustedUser = hasEdpWriteAccess));
  }

  public onRecordActivity(
    recordId: number,
    catalog: ColumnFieldAndParent[],
    fieldName: string,
    record: any,
    newValue: any, // used only to build the field name
    activity: 'update' | 'create' | 'delete' = 'update',
    id?: number
  ): { recordActivity: EntityRecordActivity; builtName: string } | undefined {
    return this.isTrustedUser
      ? this.doRecordActivity(recordId, catalog, fieldName, record, newValue, activity, id)
      : null;
  }

  getRecord = record => record;

  private doRecordActivity(
    recordId: number,
    catalog: ColumnFieldAndParent[],
    fieldName: string,
    record: any,
    newValue: any, // used only to build the field name
    activity: 'update' | 'create' | 'delete' = 'update',
    index: number
  ): { recordActivity: EntityRecordActivity; builtName: string } | undefined {
    const { parent } = catalog.find(item => item.field === fieldName);
    if (parent[activity] == null) {
      return;
    }
    // record.data is coming from reports
    const recordData = this.getRecord(record);
    const { entity, idField } = parent;
    const { method, fields } = parent[activity];
    const generatedFieldName = this.buildFieldName(recordData, fieldName, method, idField, newValue, index);
    if (!this.entityActivityLog[recordId]) {
      this.entityActivityLog[recordId] = {};
    }
    if (this.validateOnDelete(activity, recordId, generatedFieldName)) {
      delete this.entityActivityLog[recordId][generatedFieldName];
      return; // can't return previous line because returns true and a null is required.
    }

    // if simple method already exists.
    if (
      generatedFieldName &&
      !generatedFieldName.includes('=') &&
      !!this.entityActivityLog[recordId][generatedFieldName]
    ) {
      const existentActivityLog = this.entityActivityLog[recordId][generatedFieldName];
      // assign a new value.
      this.entityActivityLog[recordId][generatedFieldName] = {
        // set current values
        ...existentActivityLog,
        // update the fields object
        fields: {
          // add current fields.
          ...existentActivityLog.fields,
          // filter out existing fields
          ...Object.entries(fields)
            .filter(
              ([fieldKey]) =>
                !Object.keys(existentActivityLog.fields).some(currentFieldKeys => currentFieldKeys === fieldKey)
            )
            .reduce((acc, [fieldKey, valueKey]) => ({ ...acc, [fieldKey]: valueKey }), {}),
        },
      };
    } else if (generatedFieldName) {
      const currentActivity = this.entityActivityLog[recordId][generatedFieldName] ?? {};
      const existingFieldNames = currentActivity['fieldName'] || [];

      // Only add if not already present
      const updatedFieldNames = existingFieldNames.includes(fieldName)
        ? existingFieldNames
        : [...existingFieldNames, fieldName];

      const nextActivity = deepMerge(currentActivity, {
        method,
        fields,
        entity,
        recordId: idField,
        fieldName: updatedFieldNames,
      });
      this.entityActivityLog[recordId][generatedFieldName] = nextActivity as EntityRecordActivity;

      if (activity === 'create') {
        this.entityActivityLog[recordId][generatedFieldName]['isNew'] = true;
      }
      return {
        recordActivity: { ...this.entityActivityLog[recordId][generatedFieldName] },
        builtName: generatedFieldName,
      };
    }
    return null;
  }

  public onDelete(
    recordId: number,
    catalog: RawColumn[],
    fieldName: string,
    record: any,
    deletedValue: any // used only to build the field name
  ): { recordActivity: EntityRecordActivity; builtName: string } {
    return this.isTrustedUser ? this.doDelete(recordId, catalog, fieldName, record, deletedValue) : null;
  }

  public doDelete(
    recordId: number,
    catalog: RawColumn[],
    fieldName: string,
    record: any,
    deletedValue: any // used only to build the field name
  ): { recordActivity: EntityRecordActivity; builtName: string } {
    const onRecordActivityResponse = this.onRecordActivity(
      recordId,
      catalog,
      fieldName,
      record,
      deletedValue,
      'delete'
    );
    if (onRecordActivityResponse == null) {
      return;
    }
    const { recordActivity, builtName } = onRecordActivityResponse;

    this.entityActivityLog[recordId][builtName].fields = this.fetchRecordData(
      record,
      recordActivity,
      builtName.split('?')[1].split('&')
    );
    this.entityActivityLog[recordId][builtName].isFilled = true;

    return { recordActivity: { ...this.entityActivityLog[recordId][builtName] }, builtName };
  }

  /**
   * @param records
   * @returns true if there were any errors
   */
  public onSave(records: any[], isLeaseSpacesView?: boolean, view?: string): Observable<boolean> {
    return this.onSaveAndGetErrors(records, isLeaseSpacesView, view).pipe(
      map((failedIdsArray: any[]) => {
        return Object.keys(this.entityActivityLog).length > 0 || failedIdsArray.length > 0;
      })
    );
  }

  public onSaveAndGetErrors(
    records: any[],
    isLeaseSpacesView?: boolean,
    view?: string | null,
    selectedEntity?: EntityType
  ): Observable<any[]> {
    const requestsObject = this.buildRequestsObject(records, isLeaseSpacesView, view, selectedEntity);
    return concat(...Object.values(requestsObject)).pipe(
      toArray(),
      map((failedIds: number[][]) => {
        const failedIdsArray = Array.from(
          new Set(failedIds.reduce((acc, val) => [...acc, ...val.map(failed => failed.toString())], []))
        );
        this.entityActivityLog = Object.entries(this.entityActivityLog)
          .filter(entry => failedIdsArray.includes(entry[0]))
          .reduce((acc, entry) => ({ [entry[0]]: entry[1], ...acc }), {});
        return failedIdsArray;
      })
    );
  }

  protected buildRequestsObject(
    records: any[],
    isLeaseSpacesView?: boolean,
    view?: string | null,
    _selectedEntity?: EntityType
  ): { [recordId: string]: Observable<any> } {
    // get entries from the main entityActivityLog. The entries are the recordID and the recordActivityLog
    const requests = Object.entries(this.entityActivityLog).map(
      ([recordId, recordActivityLog]: [string, EntityRecordActivityLog]) => [
        recordId,
        // map the array of entries into an array of arrays of http requests (observables).
        // get entries of the recordActivityLog. The entries are the built name and each record activity.
        Object.entries(recordActivityLog)
          .filter(([builtFieldName]) => {
            // Skip processing floor_suite/updateLeaseArrangementPropertyAssociation entries when view is "Spaces View"
            if (
              view === SubTabsFilterOptions.SpacesView &&
              builtFieldName.includes('floor_suite/updateLeaseArrangementPropertyAssociation')
            ) {
              return false;
            }
            return true;
          })
          .map(([builtFieldName, recordActivity]: [string, EntityRecordActivity]) => {
            const path = isLeaseSpacesView ? 'recordMetadata.recordId' : 'entity.id';
            const record = records.find(eachRecord => digger(eachRecord, path).toString() === recordId);
            // map the array of entries into an array of http requests (observables).
            const { method, entity, fieldName } = recordActivity;
            // get the complex field id value or just assign a undefined value —handled on the fetchRecordData.
            const idFieldValues = builtFieldName.includes('=') ? builtFieldName.split('?')[1].split('&') : undefined;
            // fetch real data from the passed record.
            // create the request (observable).
            const payload = isLeaseSpacesView
              ? makeLeaseSpaceTemplatedRequest(record, records, fieldName)
              : this.fetchRecordData(record, recordActivity, idFieldValues);
            return this.httpService.updateRecordOnEDP(entity, recordId, payload, method, null, isLeaseSpacesView).pipe(
              map(({ id }) => ({ succeeded: id })),
              catchError(obj => {
                const id = obj && obj.id ? obj.id : undefined;
                return of({ failed: id ? id : recordId });
              })
            );
          }),
      ]
    );
    // transform the array of arrays into an object based on record id and a observable.
    const digestedRequest = this.digestedEntries.map(([taskKey, data]) => {
      const [entity, recordId, method] = taskKey.split('.');
      return [
        taskKey,
        [
          this.httpService.updateRecordOnEDP(entity, recordId, data, method).pipe(
            map(({ id }) => ({ succeeded: id })),
            catchError(obj => of({ failed: obj?.id ? obj.id : obj }))
          ),
        ],
      ];
    });
    return [...requests, ...digestedRequest].reduce(
      (acc, entry: [string, Array<Observable<any>>]) => ({
        // create and return an observable from the array of observables.
        ...acc,
        [entry[0]]: EdpRecordsUpdateService.concatEDPRequestsAndMapFailed(entry[1]),
      }),
      {}
    );
  }

  /**
   * Makes an array of Mutation objects ({method, args}) and calls to `bulkmsg/pushAsync` endpoint
   * @param records
   * @returns
   */
  public bulkSave(
    records: DataObject[],
    localizedEntity: string,
    entity: EntityType,
    isLeaseSpacesView?: boolean,
    transactionSourceSuffix?: string
  ): Observable<any> {
    const payloads = this._makePayloadObjects(records, isLeaseSpacesView);
    return this.httpService
      .bulkUpdate(payloads, localizedEntity, entity, transactionSourceSuffix)
      .pipe(tap(() => this.resetEntityActivityLog()));
  }

  public bulkSaveWithAdditionalPayload(
    records: DataObject[],
    additionalPayloads: DataObject[],
    entity: EntityType,
    view: string
  ): Observable<BulkEditSaveResult> {
    const payloads = this.createLeaseSpacePayload(additionalPayloads);
    const originalActivityLog = this.entityActivityLog;
    const originalFieldNamesByRecordId = Object.fromEntries(
      Object.entries(originalActivityLog).map(([recordId, activityLog]) => [
        recordId,
        [...new Set(Object.values(activityLog).flatMap(activity => activity.fieldName ?? []))],
      ])
    );
    const pendingRecordCount = Object.keys(originalActivityLog).length;
    // /write/space requires a positive top-level property_id/property_usage_id and an id on every suite;
    // processGroupedLeases only lifts those fields when all of a lease's suites share one property.
    const isDirectSpaceCompatible = (payload: DataObject) =>
      payload.args?.property_id > 0 &&
      payload.args?.property_usage_id > 0 &&
      (payload.args?.floor_suite ?? []).every((suite: DataObject) => suite?.id != null);
    const direct =
      pendingRecordCount > 0 &&
      pendingRecordCount <= DIRECT_SPACE_PAYLOAD_MAX_RECORDS &&
      payloads.every(isDirectSpaceCompatible);

    const spaceSave$ = direct
      ? this.saveLeaseSpacePayloadsDirect(payloads, entity).pipe(
          map(failedRecordIds => ({ failedRecordIds, hasErrors: failedRecordIds.length > 0 }))
        )
      : payloads.length > 0
        ? this.httpService.bulkUpdate(payloads, 'LEASE COMPS', entity, 'Property Comp Spaces').pipe(
            map(() => ({ failedRecordIds: [], hasErrors: false })),
            catchError(() => of({ failedRecordIds: [], hasErrors: true }))
          )
        : of({ failedRecordIds: [], hasErrors: false });

    return forkJoin([this.onSave(records, false, view), spaceSave$]).pipe(
      map(([leasesHasErrors, spaceResult]) => {
        const failedRecordIds = new Set([...Object.keys(this.entityActivityLog), ...spaceResult.failedRecordIds]);
        const hasErrors = !!leasesHasErrors || spaceResult.hasErrors;

        if (direct && hasErrors) {
          this.entityActivityLog = Object.fromEntries(
            [...failedRecordIds]
              .filter(recordId => originalActivityLog[recordId] != null)
              .map(recordId => [recordId, originalActivityLog[recordId]])
          );
        }

        return {
          direct,
          hasErrors,
          failedFieldsByRecordId:
            direct && hasErrors
              ? Object.fromEntries(
                  [...failedRecordIds]
                    .filter(recordId => originalFieldNamesByRecordId[recordId]?.length)
                    .map(recordId => [recordId, originalFieldNamesByRecordId[recordId]])
                )
              : undefined,
        };
      })
    );
  }

  private saveLeaseSpacePayloadsDirect(payloads: DataObject[], entity: EntityType): Observable<string[]> {
    const requests = payloads.map(({ method, args }) =>
      this.httpService.updateRecordOnEDP(entity, String(args.lease_id), args, method, null, true).pipe(
        map(() => ({ succeeded: args.lease_id })),
        catchError(() => of({ failed: String(args.lease_id) }))
      )
    );
    return EdpRecordsUpdateService.concatEDPRequestsAndMapFailed(requests).pipe(
      map(failedIds => failedIds.flat().map(recordId => String(recordId)))
    );
  }

  protected createLeaseSpacePayload(additionalPayloads: DataObject[]): DataObject[] {
    const payloads = Object.entries(this.entityActivityLog).map(
      ([recordId, recordActivityLog]: [string, EntityRecordActivityLog]) =>
        Object.values(recordActivityLog)
          .filter(
            (recordActivity: EntityRecordActivity) =>
              recordActivity.method === 'updateLeaseArrangementPropertyAssociation'
          )
          .map((recordActivity: EntityRecordActivity) =>
            this.makeLeaseSpacePayload(recordActivity, recordId, additionalPayloads)
          )
    );
    const flattenedPayload = payloads.reduce((acc, next) => [...acc, ...next], []);
    const groupedByLeaseId = this.groupPayloadsByLeaseId(flattenedPayload);
    const processedLeases = this.processGroupedLeases(groupedByLeaseId);
    return processedLeases;
  }

  makeLeaseSpacePayload(
    recordActivity: EntityRecordActivity,
    recordId: string,
    additionalPayloads: DataObject[]
  ): DataObject {
    const floorSuiteKeys = ['floor_number', 'suite_number'];
    const record = additionalPayloads.find(eachRecord => eachRecord.recordId.toString() === recordId);
    const { fieldName } = recordActivity;
    // clone the propAssociation to avoid mutating the original additionalPayloads object
    const originalFloor = digger(record.args, 'propAssociation');
    const floor = deepCloneObject(originalFloor);
    floor.hasFloorSuiteChanged = floorSuiteKeys.some(key => fieldName.some(field => field.includes(key)));
    if (!floor.hasFloorSuiteChanged) {
      floor.floor_suite = (floor.floor_suite || []).map(fs => {
        const clonedFs = { ...fs };
        // Remove the fields that are not needed for the updateLeaseArrangementPropertyAssociation payload
        delete clonedFs.alternate_address;
        delete clonedFs.suite_id;
        delete clonedFs.floor_id;
        delete clonedFs.suite_number;
        delete clonedFs.floor_number;
        delete clonedFs.miqProperties;
        return clonedFs;
      });
      delete floor.miqProperties;
    }
    return { method: 'miqUpdateSpaces', args: floor };
  }

  public removeFromEntityActivityLog(recordId: number) {
    delete this.entityActivityLog[recordId];
  }

  /**
   * Gets a summary of the activity log for all records, including the record ID, method, and field name.
   * @returns An array of ActivityLogSummary objects.
   */
  public getActivityLogSummary(): ActivityLogSummary[] {
    const complteSummary = Object.keys(this.entityActivityLog).reduce(
      (wholeDetails: ActivityLogSummary[], cKey: string) => {
        const recordMethodDetails = this.entityActivityLog[cKey];
        const summary = Object.keys(recordMethodDetails).reduce((recordDetails: ActivityLogSummary[], methodKey) => {
          const payloadDetails = recordMethodDetails[methodKey];
          const fieldName = Array.isArray(payloadDetails.fieldName) ? payloadDetails.fieldName[0] : '';
          recordDetails.push({ id: Number(cKey), method: payloadDetails.method, fieldName: fieldName });
          return recordDetails;
        }, []);

        return [...wholeDetails, ...summary];
      },
      []
    );

    return complteSummary;
  }

  public getActivityLog() {
    return this.entityActivityLog;
  }

  /**
   * Edited field names on records not confirmed as successful, keyed by record ID.
   * Only meaningful right after `onSave`/`onSaveAndGetErrors` resolves with failures — that call
   * filters `entityActivityLog` down to failed records for retry, which this reads. Failure status
   * is tracked per record, so this includes every edited field on each failed record.
   */
  public getFailedFieldNamesByRecord(): Record<string, string[]> {
    return Object.entries(this.entityActivityLog).reduce(
      (acc, [recordId, recordActivityLog]) => {
        const fieldNames = Object.values(recordActivityLog).flatMap(activity => activity.fieldName ?? []);
        return fieldNames?.length ? { ...acc, [recordId]: fieldNames } : acc;
      },
      {} as Record<string, string[]>
    );
  }

  public resetEntityActivityLog() {
    this.entityActivityLog = {};
  }

  /**
   * Removes all actions related to a specific field for a given record.
   * This will remove the whole key so it won't work for attributes that share the same payload/method
   * @param recordId - The ID of the record.
   * @param fieldName - The name of the field to remove actions for.
   * @returns void
   */
  public removeFromEntityActivityLogByField(recordId: number, fieldName: string) {
    // Examples:
    // Tags: { 9999: { addUserTag: { fieldName: ['user_tag/name'] }}}
    const recordActivityLog = this.entityActivityLog[recordId];
    if (!recordActivityLog) {
      return;
    }
    // Remove all the related actions to cover add and delete actions
    const activityKeys = Object.keys(recordActivityLog).filter(key =>
      recordActivityLog[key].fieldName?.some((field: string) => field === fieldName)
    );
    activityKeys.forEach(activityKey => {
      delete this.entityActivityLog[recordId][activityKey];
    });
  }

  /**
   * Removes all actions related to a specific method for a given record.
   * @param recordId - The ID of the record.
   * @param method - The name of the method to remove actions for.
   * @returns void
   */
  public removeFromEntityActivityLogByMethod(recordId: number, method: string) {
    if (recordId == null || method == null) {
      return;
    }

    // Examples:
    // Tags: { 9999: { addUserTag: { fieldName: ['user_tag/name'] }}}
    const recordActivityLog = this.entityActivityLog[recordId];
    if (!recordActivityLog || recordActivityLog?.[method] == null) {
      return;
    }

    // Clean up the whole payload for the selected record & method
    delete this.entityActivityLog[recordId][method];
  }

  /**
   * Makes, flats and returns an array of Mutation objects ({method, args})
   * @param records
   * @returns
   */
  protected _makePayloadObjects(records: DataObject[], isLeaseSpacesView?: boolean): DataObject[] {
    const payloads = Object.entries(this.entityActivityLog).map(
      ([recordId, recordActivityLog]: [string, EntityRecordActivityLog]) =>
        Object.values(recordActivityLog).map((recordActivity: EntityRecordActivity) =>
          this._makePayloadObject(records, recordId, recordActivity, isLeaseSpacesView)
        )
    );
    const flattenedPayload = payloads.reduce((acc, next) => [...acc, ...next], []);

    if (isLeaseSpacesView) {
      const groupedByLeaseId = this.groupPayloadsByLeaseId(flattenedPayload);
      const processedLeases = this.processGroupedLeases(groupedByLeaseId);
      return processedLeases;
    }

    return flattenedPayload;
  }

  /**
   * Groups flattened payloads by lease_id, merging floor_suite arrays and preserving hasFloorSuiteChanged flag
   */
  protected groupPayloadsByLeaseId(flattenedPayload: any[]): { [leaseId: string]: any } {
    return flattenedPayload.reduce((acc, payload) => {
      if (!payload.args?.lease_id) {
        return acc;
      }

      const leaseId = payload.args.lease_id;

      if (!acc[leaseId]) {
        acc[leaseId] = { ...payload };
      } else {
        acc[leaseId] = this.mergeLeasePayloads(acc[leaseId], payload);
      }

      return acc;
    }, {});
  }

  /**
   * Merges two lease payloads, combining floor_suite arrays and preserving hasFloorSuiteChanged flag
   */
  private mergeLeasePayloads(existingPayload: any, newPayload: any): any {
    const existingFloorSuite = existingPayload.args.floor_suite || [];
    const newFloorSuite = newPayload.args.floor_suite || [];

    const existingHasChanged = existingPayload.args.hasFloorSuiteChanged || false;
    const newHasChanged = newPayload.args.hasFloorSuiteChanged || false;

    // Use destructuring assignment to exclude floor_suite from newArgs

    const { floor_suite: _, ...payloadArgsWithoutFloorSuite } = newPayload.args;
    const mergedArgs = { ...existingPayload.args, ...payloadArgsWithoutFloorSuite };

    // Preserve hasFloorSuiteChanged if true in either payload
    if (existingHasChanged || newHasChanged) {
      mergedArgs.hasFloorSuiteChanged = true;
    }

    // Combine floor_suite arrays
    mergedArgs.floor_suite = [...existingFloorSuite, ...newFloorSuite];

    return { ...existingPayload, args: mergedArgs };
  }

  /**
   * Processes grouped leases to extract common property fields and merge floor suites
   */
  protected processGroupedLeases(groupedByLeaseId: { [leaseId: string]: any }): any[] {
    Object.values(groupedByLeaseId).forEach((leasePayload: any) => {
      if (leasePayload.args?.floor_suite?.length > 0) {
        this.processLeaseFloorSuites(leasePayload);
      }
    });

    return Object.values(groupedByLeaseId);
  }

  /**
   * Processes floor suites for a single lease, merging duplicates and extracting common properties
   */
  private processLeaseFloorSuites(leasePayload: any): void {
    const floorSuites = leasePayload.args.floor_suite;
    const mergedFloorSuites = this.mergeFloorSuitesById(floorSuites);

    if (this.allSuitesHavePropertyFields(mergedFloorSuites)) {
      this.extractCommonPropertyFields(leasePayload, mergedFloorSuites);
    } else {
      leasePayload.args.floor_suite = mergedFloorSuites;
    }
  }

  /**
   * Merges floor suite items by their id, creating unique keys for items without ids
   */
  private mergeFloorSuitesById(floorSuites: any[]): any[] {
    const groupedFloorSuites = floorSuites.reduce((acc, suite) => {
      const key = suite.id ? suite.id : `no_id_${Object.keys(acc).length}`;

      if (!acc[key]) {
        acc[key] = suite;
      } else {
        acc[key] = { ...acc[key], ...suite };
      }

      return acc;
    }, {});

    return Object.values(groupedFloorSuites);
  }

  /**
   * Checks if all floor suites have property_id and property_usage_id fields
   */
  private allSuitesHavePropertyFields(mergedFloorSuites: any[]): boolean {
    return mergedFloorSuites.every(
      suite => suite.hasOwnProperty('property_id') && suite.hasOwnProperty('property_usage_id')
    );
  }

  /**
   * Extracts common property fields if all suites share the same values, otherwise updates with merged suites
   */
  private extractCommonPropertyFields(leasePayload: any, mergedFloorSuites: any[]): void {
    const firstSuite = mergedFloorSuites[0];
    const commonPropertyId = firstSuite.property_id;
    const commonPropertyUsageId = firstSuite.property_usage_id;

    const allSuitesHaveSameProperties = mergedFloorSuites.every(
      suite => suite.property_id === commonPropertyId && suite.property_usage_id === commonPropertyUsageId
    );

    if (allSuitesHaveSameProperties) {
      this.movePropertyFieldsToLeaseLevel(leasePayload, mergedFloorSuites, commonPropertyId, commonPropertyUsageId);
    } else {
      leasePayload.args.floor_suite = mergedFloorSuites;
    }
  }

  /**
   * Moves property_id and property_usage_id from floor suites to lease level and removes them from suites
   */
  private movePropertyFieldsToLeaseLevel(
    leasePayload: any,
    mergedFloorSuites: any[],
    commonPropertyId: any,
    commonPropertyUsageId: any
  ): void {
    leasePayload.args.property_id = commonPropertyId;
    leasePayload.args.property_usage_id = commonPropertyUsageId;

    leasePayload.args.floor_suite = mergedFloorSuites.map(suite => {
      return (({ _property_id, _property_usage_id, ...rest }) => rest)(suite);
    });
  }

  /**
   * Makes and returns one Mutation object ({method, args})
   * @param records
   * @param recordId
   * @param recordActivity
   * @returns
   */
  protected _makePayloadObject(
    records: DataObject[],
    recordId: string,
    recordActivity: EntityRecordActivity,
    isLeaseSpacesView?: boolean
  ): DataObject {
    const path = isLeaseSpacesView ? 'recordMetadata.recordId' : 'entity.id';
    const record = records.find(eachRecord => digger(eachRecord, path).toString() === recordId);
    // map the array of entries into an array of http requests (observables).
    const { method, fields, fieldName } = recordActivity;

    if (isLeaseSpacesView) {
      const leaseSpacesArgs = makeLeaseSpaceTemplatedRequest(record, records, fieldName);

      return { method: 'miqUpdateSpaces', args: leaseSpacesArgs };
    }

    // get the complex field id value or just assign a undefined value —handled on the fetchRecordData.
    const args = makeTemplatedRequest(record, fields);

    // make bulk edit payload.
    return { method, args };
  }

  public executeSingleDigestRequest(recordId, { entity, data, method }): Observable<any> {
    return this.createWriteHttpCall(entity, recordId, data, method);
  }

  private createWriteHttpCall(entity, recordId, data, method): Observable<any> {
    return this.httpService
      .updateRecordOnEDP(entity, recordId, data, method)
      .pipe(catchError(obj => of({ failed: obj ? obj.id : obj })));
  }

  public addDigestedActivity(recordId, { entity, identifier, data, method }) {
    const actionKey = `${entity}.${recordId}.${method}.${identifier}`;
    if (!this.digestedRequests.h.some(x => x === actionKey)) {
      this.digestedRequests.h = [...this.digestedRequests.h, actionKey];
    }
    const current = this.digestedRequests[actionKey] || {};
    const combineMerge = (target, source, options) => {
      const destination = target.slice();
      source.forEach((item, index) => {
        if (typeof destination[index] === 'undefined') {
          destination[index] = options.cloneUnlessOtherwiseSpecified(item, options);
        } else if (options.isMergeableObject(item)) {
          destination[index] = deepmerge(target[index], item, options);
        } else if (target.indexOf(item) === -1) {
          destination.push(item);
        }
      });
      return destination;
    };
    return (this.digestedRequests[actionKey] = deepmerge(current, data, { arrayMerge: combineMerge }));
  }

  public deleteDigestedActivity(fullTaskId) {
    delete this.digestedRequests[fullTaskId];
    this.digestedRequests.h = this.digestedRequests.h.filter(actionId => actionId !== fullTaskId);
  }

  public deleteDigestedActivityWithParams({ entity, recordId, method, identifier }) {
    this.deleteDigestedActivity(`${entity}.${recordId}.${method}.${identifier}`);
  }

  public clearDigestedActivity() {
    this.digestedRequests = { h: [] };
  }

  protected get digestedEntries(): any[] {
    return this.digestedRequests.h.map(key => [key, this.digestedRequests[key]]);
  }

  public edpTranslator(fields: any, data: any) {
    const transmutator = Object.entries(fields).reduce(
      (acc, [edpField, diggerField]: any[]) => ({
        ...acc,
        [diggerField.substring(diggerField.lastIndexOf('/') + 1)]: edpField,
      }),
      {}
    );
    return Object.entries(transmutator).reduce(
      (acc, [key, value]) => ({ ...acc, [`${value}`]: digger(data, key) }),
      {}
    );
  }

  protected fetchRecordData(
    record: any,
    { fields: requiredFields, recordId: idFieldPath, isFilled }: EntityRecordActivity,
    idFieldValues?: string[]
  ): EntityRecordReqFields {
    if (
      idFieldValues == null &&
      Object.values(requiredFields).some(key => {
        return typeof key !== 'object' && key.includes('/');
      })
    ) {
      return;
    } else if (isFilled) {
      return requiredFields;
    }
    // currently this block will only support array of length 1 and object that has source_id
    return Object.entries(requiredFields)
      .map(([KEY, VALUE]) => {
        let value: any;
        if (Array.isArray(VALUE)) {
          value =
            idFieldValues != null
              ? [this.fetchArrayData(record, idFieldValues, VALUE[0], idFieldPath)]
              : [digger(record, VALUE[0])];
        } else if (typeof VALUE === 'object') {
          value = { source_id: digger(record, VALUE['source_id']) };
        } else if (VALUE.includes('/') && idFieldPath != null && idFieldValues != null) {
          value = this.fetchArrayData(record, idFieldValues, VALUE, idFieldPath);
        } else {
          value = digger(record, VALUE);
        }
        return [KEY, value];
      })
      .reduce((acc, entry) => ({ ...acc, [entry[0]]: entry[1] }), {});
  }

  protected fetchArrayData(record: any, idFieldValues: string[], reqFieldPath: string, idFieldPath: string): any {
    const reqFieldPathArr = reqFieldPath.split('/');
    const idFieldNames = idFieldPath.includes(',')
      ? idFieldPath
          .split(',')
          .map(idField => idField.split('/'))
          .map(f => f[f.length - 1])
      : [idFieldPath.split('/').splice(-1, 1)[0]];
    const reqFieldName = reqFieldPathArr.splice(-1, 1)[0];
    const arrayPath = reqFieldPathArr.join('/');
    // if at least one of idField starts with array path
    if (!idFieldPath.split(',').some(x => x.split('/')[0] === arrayPath)) {
      return;
    }
    const subsetElements = digger(record, arrayPath);
    return digger(
      subsetElements.find((complexField: { [x: string]: any }) =>
        idFieldNames
          .map(idField => `${idField}=${digger(complexField, idField)}`)
          .reduce((acc, val) => acc && idFieldValues.includes(val), true)
      ),
      reqFieldName
    );
  }

  /**
   * If fieldName contains a '/', the method should
   * split and then join everything but the last element
   * to use it to retrieve the whole array of data.
   * Then it should search, using the last element,
   * the item that contains the value.
   *
   * It shall split the idFieldName by '/' and use the
   * last item to get the value of the id. The method
   * shall return the following concatenation:
   *
   * `${fieldName}?${idFieldName}=${idValue}`
   *
   * Example (not real field):
   * 'phone/source_lineage/test_new_field?phone/source_lineage/test_id_field=123'
   * Example when multiple idFields:
   * 'company_role_addresses.company_id=1232&company_role_addresses.location_id=12312&company_role_addresses.role_desc=Contact'
   */
  private buildFieldName(
    record: { [key: string]: any },
    fieldNamePath: string,
    method: string,
    idFieldNamePath?: string,
    updatedValue?: any,
    id?: number
  ): string {
    if (!fieldNamePath.includes('/')) {
      return method;
    }

    let idFieldPaths = [idFieldNamePath];
    if (idFieldNamePath && idFieldNamePath.includes(',')) {
      idFieldPaths = idFieldNamePath.split(',');
    }

    const fieldName = fieldNamePath.split('/');
    const idFieldNames = idFieldPaths.map(idField => idField.split('/').slice(-1)[0]);
    const valueFieldName = fieldName.slice(-1)[0];
    let editedObject: any = null;
    const values: any[] = digger(record, fieldName.slice(0, -1).join('/'));
    // if the id is sent, pick the first occurance of the id
    if (id) {
      editedObject = values.find(val => digger(val, 'id') === id);
    } else {
      // if the id doesn't exist, then pick the last occurance
      const valuesCount = values.length;
      for (let valueIndex = valuesCount - 1; valueIndex > -1; valueIndex--) {
        if (
          typeof updatedValue === 'object' &&
          idFieldNames.every(field => digger(values[valueIndex], field) === digger(updatedValue, field))
        ) {
          editedObject = values[valueIndex];
          break;
        } else if (digger(values[valueIndex], valueFieldName) === updatedValue) {
          editedObject = values[valueIndex];
          break;
        }
      }
    }
    const idValues =
      editedObject != null ? idFieldNames.map(idField => `${idField}=${digger(editedObject, idField)}`) : undefined;

    const commonPath = fieldName.slice(0, fieldName.length - 1).join('/');

    return idValues != null ? `${commonPath}/${method}?${idValues.join('&')}` : null;
  }

  public validateOnDelete(activity: string, recordId: number, generatedFieldName: string) {
    return (
      activity === 'delete' &&
      !!this.entityActivityLog[recordId][generatedFieldName] &&
      this.entityActivityLog[recordId][generatedFieldName]['isNew']
    );
  }
}
